/**
 * AutoCAD hatch pattern (.pat) file support.
 *
 * A .pat file carries named hatch pattern definitions. Each definition is a
 * `*NAME, description` header followed by one line family per row:
 *
 *   angle, originX, originY, deltaX, deltaY [, dash1, dash2, ...]
 *
 * - `angle`        family direction, degrees CCW from +X
 * - `originX/Y`    anchor of the first line of the family
 * - `deltaX/Y`     shift from one parallel line to the next (deltaY is the
 *                  perpendicular spacing in the family's own frame)
 * - `dashes`       positive = draw, negative = gap, 0 = dot; omitted = solid
 *
 * The same engine renders imported files AND the app's built-in patterns, so
 * HATCH / HATCHEDIT / Filled Regions all share one pattern pipeline. Imported
 * definitions shadow built-ins of the same name (first occurrence wins, like
 * AutoCAD resolving duplicate names in its support-path search).
 */

export type PatHatchPoint = readonly [number, number, number?];

export interface PatLineDef {
    /** Family direction in degrees CCW from +X (may be negative). */
    angleDeg: number;
    /** Anchor of the first line of the family, pattern units. */
    originX: number;
    originY: number;
    /** Shift from one parallel line to the next, pattern units. */
    deltaX: number;
    deltaY: number;
    /** Positive = draw, negative = gap, 0 = dot; empty = solid line. */
    dashes: number[];
}

export interface PatPattern {
    /** Lookup name, kept verbatim (resolution is case-insensitive). */
    name: string;
    /** Free text after the comma in the header. */
    description: string;
    lines: PatLineDef[];
}

export interface PatHatchOptions {
    /** Uniform multiplier applied to origins/deltas (HPSCALE analog). */
    scale?: number;
    /** Extra rotation applied to every family angle, degrees. */
    rotationDeg?: number;
    /** Safety cap so a pathologically dense pattern cannot stall a frame. */
    maxSegments?: number;
}

/** Read a picked .pat file honoring its encoding (BOM sniff, UTF-8 fallback). */
export async function readPatFile(file: File): Promise<string> {
    const bytes = new Uint8Array(await file.arrayBuffer());
    if (bytes[0] === 0xff && bytes[1] === 0xfe) {
        return new TextDecoder('utf-16le').decode(bytes);
    }
    if (bytes[0] === 0xfe && bytes[1] === 0xff) {
        return new TextDecoder('utf-16be').decode(bytes);
    }
    return new TextDecoder('utf-8').decode(bytes);
}

/**
 * Parse a .pat file into pattern definitions. `;` starts a comment anywhere
 * on a line, a trailing `\` continues the definition on the next line, and
 * blank lines are skipped. Malformed definition lines are dropped; the first
 * definition of a duplicated name wins (AutoCAD support-path semantics).
 */
export function parsePatDefinitions(text: string): PatPattern[] {
    const patterns: PatPattern[] = [];
    let current: PatPattern | null = null;
    let carry: string | null = null;

    for (const raw of text.replace(/\r\n?/g, '\n').split('\n')) {
        let line = raw.split(';')[0].trim();
        if (carry !== null) {
            line = `${carry} ${line}`.trim();
            carry = null;
        }
        if (line === '') continue;
        if (line.endsWith('\\')) {
            carry = line.slice(0, -1).trim();
            continue;
        }
        if (line.startsWith('*')) {
            const header = parsePatHeader(line);
            current = null;
            if (!header) continue;
            const dup = patterns.some((p) => p.name.toUpperCase() === header.name.toUpperCase());
            if (!dup) {
                patterns.push(header);
                current = header;
            }
            continue;
        }
        if (!current) continue;
        const def = parsePatLineDef(line);
        if (def) current.lines.push(def);
    }
    // Headers with no valid definition row (or only malformed rows) would
    // render as an empty pattern — drop them.
    return patterns.filter((p) => p.lines.length > 0);
}

function parsePatHeader(line: string): PatPattern | null {
    const body = line.slice(1).trim();
    if (body === '') return null;
    const comma = body.indexOf(',');
    const name = (comma === -1 ? body : body.slice(0, comma)).trim();
    if (name === '') return null;
    const description = comma === -1 ? '' : body.slice(comma + 1).trim();
    return { name, description, lines: [] };
}

function parsePatLineDef(line: string): PatLineDef | null {
    const nums = line.split(',').map((tok) => Number(tok.trim()));
    if (nums.length < 5 || nums.some((n) => !Number.isFinite(n))) return null;
    const [angleDeg, originX, originY, deltaX, deltaY] = nums;
    if (deltaX === 0 && deltaY === 0) return null;
    return { angleDeg, originX, originY, deltaX, deltaY, dashes: nums.slice(5) };
}

/**
 * App built-ins. App-native names (Diagonal Crosshatch / Concrete /
 * Insulation) reproduce the legacy hand-coded region hatchings exactly;
 * ANSI31/ANSI37/DOTS carry their stock acad.pat definitions so imported
 * files and built-ins render identically.
 */
export const BUILT_IN_PAT_PATTERNS: PatPattern[] = [
    {
        name: 'Diagonal Crosshatch',
        description: 'Two crossing 45/135 line sets',
        lines: [
            { angleDeg: 45, originX: 0, originY: 0, deltaX: 0, deltaY: 0.15, dashes: [] },
            { angleDeg: 135, originX: 0, originY: 0, deltaX: 0, deltaY: 0.15, dashes: [] },
        ],
    },
    {
        name: 'ANSI31',
        description: 'ANSI Iron, Brick, Refractory Brick',
        lines: [
            { angleDeg: 45, originX: 0, originY: 0, deltaX: 0, deltaY: 0.125, dashes: [] },
        ],
    },
    {
        name: 'ANSI37',
        description: 'ANSI Steel, Energy, Sand, Rubber, Gypsum',
        lines: [
            { angleDeg: 45, originX: 0, originY: 0, deltaX: 0, deltaY: 0.0625, dashes: [] },
            { angleDeg: 45, originX: 0.0625, originY: 0, deltaX: 0, deltaY: 0.0625, dashes: [] },
            { angleDeg: 135, originX: 0, originY: 0, deltaX: 0, deltaY: 0.0625, dashes: [] },
            { angleDeg: 135, originX: 0, originY: 0.0625, deltaX: 0, deltaY: 0.0625, dashes: [] },
        ],
    },
    {
        name: 'Concrete',
        description: 'Tri-line concrete stipple',
        lines: [
            { angleDeg: 30, originX: 0, originY: 0, deltaX: 0, deltaY: 0.2, dashes: [] },
            { angleDeg: 120, originX: 0, originY: 0, deltaX: 0, deltaY: 0.45, dashes: [] },
            { angleDeg: -35, originX: 0, originY: 0, deltaX: 0, deltaY: 0.6, dashes: [] },
        ],
    },
    {
        name: 'Insulation',
        description: 'Dense 45 line set',
        lines: [
            { angleDeg: 45, originX: 0, originY: 0, deltaX: 0, deltaY: 0.08, dashes: [] },
        ],
    },
    {
        name: 'DOTS',
        description: 'Dot grid',
        lines: [
            { angleDeg: 0, originX: 0, originY: 0, deltaX: 0, deltaY: 0.0945, dashes: [0, -0.0945] },
        ],
    },
];

/**
 * Generate clipped hatch segments for a boundary from a pattern definition.
 *
 * For every family line (anchor + k·delta, direction `angle`), the infinite
 * line is intersected with each boundary edge exactly (no sampling); even-odd
 * crossing pairs give the inside spans, and each span is cut into dash
 * segments anchored at the line's own origin — the PAT dash alignment rule.
 * Segments come out as `[x1, y1, z1, x2, y2, z2]` tuples, the storage format
 * of CAD ledger hatch entities.
 */
export function generatePatHatchSegments(
    boundary: readonly PatHatchPoint[],
    pattern: PatPattern,
    options: PatHatchOptions = {}
): number[][] {
    const segs: number[][] = [];
    if (boundary.length < 3) return segs;

    const scale = options.scale && options.scale > 0 ? options.scale : 1;
    const rot = ((options.rotationDeg ?? 0) * Math.PI) / 180;
    const budget = options.maxSegments ?? 8000;

    const zs = boundary.map((p) => p[2] ?? 0);
    const z = zs.reduce((a, b) => a + b, 0) / zs.length;
    const xs = boundary.map((p) => p[0]);
    const ys = boundary.map((p) => p[1]);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    const bboxDiag = Math.hypot(maxX - minX, maxY - minY);
    if (!Number.isFinite(bboxDiag) || bboxDiag < 1e-12) return segs;
    const eps = 1e-9 * Math.max(1, bboxDiag);

    const edges: number[][] = [];
    for (let i = 0; i < boundary.length; i++) {
        const a = boundary[i], b = boundary[(i + 1) % boundary.length];
        edges.push([a[0], a[1], b[0], b[1]]);
    }

    for (const def of pattern.lines) {
        const ang = (def.angleDeg * Math.PI) / 180 + rot;
        const ux = Math.cos(ang), uy = Math.sin(ang);
        const nx = -uy, ny = ux;
        const ox = def.originX * scale, oy = def.originY * scale;
        let dx = def.deltaX * scale, dy = def.deltaY * scale;
        // Scale applies to dash lengths too (HPSCALE scales the whole recipe).
        const dashes = def.dashes.map((d) => d * scale);
        // Families are symmetric in delta sign (k flips with it); normalize
        // so the perpendicular step is positive and the k range is direct.
        if (dx * nx + dy * ny < 0) { dx = -dx; dy = -dy; }
        const step = dx * nx + dy * ny;
        const stepAbs = Math.abs(step);
        const singleLine = stepAbs < eps;

        // Perpendicular offsets covered by the boundary (convex hull of the
        // bbox projects onto an interval) give the family line indices.
        const rel = (px: number, py: number) => (px - ox) * nx + (py - oy) * ny;
        let rMin = Infinity, rMax = -Infinity;
        for (const [cx, cy] of [[minX, minY], [maxX, minY], [minX, maxY], [maxX, maxY]] as const) {
            const r = rel(cx, cy);
            if (r < rMin) rMin = r;
            if (r > rMax) rMax = r;
        }

        const emitLine = (bx: number, by: number) => {
            const ts: number[] = [];
            for (const [ax, ay, ex, ey] of edges) {
                const rx = ax - bx, ry = ay - by;
                const wx = ex - ax, wy = ey - ay;
                const denom = uy * wx - ux * wy;
                if (Math.abs(denom) < eps) continue;
                const t = (wx * ry - rx * wy) / denom;
                const q = (ux * ry - rx * uy) / denom;
                if (q >= 0 && q < 1) ts.push(t);
            }
            ts.sort((a, b) => a - b);
            // Merge numerically-duplicated crossings and force even pairing.
            const merged: number[] = [];
            for (const t of ts) {
                if (merged.length === 0 || t - merged[merged.length - 1] > eps) merged.push(t);
            }
            if (merged.length % 2 === 1) merged.pop();
            for (let i = 0; i + 1 < merged.length; i += 2) {
                emitSpan(bx, by, ux, uy, merged[i], merged[i + 1], dashes, stepAbs);
            }
        };

        const pushSeg = (bx: number, by: number, ux2: number, uy2: number, a: number, b: number) => {
            if (segs.length >= budget || b - a < eps) return;
            segs.push([bx + a * ux2, by + a * uy2, z, bx + b * ux2, by + b * uy2, z]);
        };

        const emitSpan = (
            bx: number, by: number, ux2: number, uy2: number,
            t0: number, t1: number, dashes: number[], refSpacing: number
        ) => {
            if (t1 - t0 < eps) return;
            if (!dashes || dashes.length === 0) {
                pushSeg(bx, by, ux2, uy2, t0, t1);
                return;
            }
            const period = dashes.reduce((a, d) => a + Math.abs(d), 0);
            if (period < eps) {
                // All-zero dash list: a single centered dot per span.
                emitDot(bx, by, ux2, uy2, t0, t1, t0, t1, refSpacing);
                return;
            }
            const parts = dashes.map((d) => ({ len: Math.abs(d), draw: d > 0, dot: d === 0 }));
            // Fast-forward the dash clock to where the span starts; each
            // family line's dashes restart at its own anchor (t = 0).
            let acc = 0;
            let idx = 0;
            let phase = ((t0 % period) + period) % period;
            for (let j = 0; j < parts.length; j++) {
                if (phase < acc + parts[j].len) { idx = j; phase -= acc; break; }
                acc += parts[j].len;
            }
            let t = t0;
            let guard = 0;
            while (t < t1 - eps && guard++ < budget * 3) {
                const part = parts[idx];
                const partEnd = Math.max(t + (part.len - phase), t);
                const segA = t;
                const segB = Math.min(partEnd, t1);
                if (part.draw) {
                    pushSeg(bx, by, ux2, uy2, segA, segB);
                } else if (part.dot) {
                    // Dot dashes are zero-length: emit a small tick centered
                    // on the dot, clamped to the inside span (not the dash's
                    // own empty extent).
                    emitDot(bx, by, ux2, uy2, segA, segB, t0, t1, refSpacing);
                }
                t = partEnd;
                phase = 0;
                idx = (idx + 1) % parts.length;
            }
        };

        const emitDot = (
            bx: number, by: number, ux2: number, uy2: number,
            a: number, b: number, spanLo: number, spanHi: number, refSpacing: number
        ) => {
            const mid = (a + b) / 2;
            const half = Math.max(1e-4, refSpacing * 0.03);
            const lo = Math.max(spanLo, mid - half);
            const hi = Math.min(spanHi, mid + half);
            pushSeg(bx, by, ux2, uy2, lo, hi);
        };

        if (singleLine) {
            emitLine(ox, oy);
        } else {
            const kFrom = Math.ceil(rMin / step) - 1;
            const kTo = Math.floor(rMax / step) + 1;
            const count = kTo - kFrom + 1;
            // Density guard: keep at most ~600 lines per family on huge spans.
            const stride = count > 600 ? Math.ceil(count / 600) : 1;
            for (let k = kFrom; k <= kTo; k += stride) {
                emitLine(ox + k * dx, oy + k * dy);
            }
        }
    }
    return segs;
}
